// ✅ MIGRATED TO V2 DESIGN SYSTEM - Volvo/Teal Theme
/**
 * Composant de gestion des Journaux d'Emails
 * Architecture 3 couches: Component → Hook → API Client → HTTP
 */

import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  Mail,
  AlertCircle,
  CheckCircle2,
  Clock,
  RefreshCw,
  Filter,
  X,
  TestTube2,
  Server,
  Info,
} from 'lucide-react';
import { Button, Spinner, Badge } from '@/components/ui-v2';
import { useEmailLogs } from '@/hooks/useEmailLogs';
import { EmailLog, EmailStatus } from '@/utils/emailLogsAPI';
import { notifications } from '@/lib/notifications';
import { logger } from '@/utils/logger';

import { useTranslation } from 'react-i18next';
interface EmailLogsManagementProps {
  onClose?: () => void;
}

export const EmailLogsManagement: React.FC<EmailLogsManagementProps> = ({ onClose }) => {
  const { t } = useTranslation();

  const {
    logs,
    statistics,
    loading,
    error,
    loadLogs,
    loadStatistics,
    sendTestEmail,
    getStatus,
    clearError,
  } = useEmailLogs({ autoLoad: true });

  const [filterStatus, setFilterStatus] = useState<EmailStatus | ''>('');
  const [filterEntityType, setFilterEntityType] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [emailServiceStatus, setEmailServiceStatus] = useState<{
    provider: string;
    configured: boolean;
  } | null>(null);

  // Card classes V2
  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1
    transition-all duration-500`;

  // Input/Select classes V2
  const selectClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none cursor-pointer`;

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const status = await getStatus();
        setEmailServiceStatus(status);
      } catch (err) {
        logger.error('Error checking email service status:', err);
      }
    };
    checkStatus();
  }, []);

  const handleApplyFilters = () => {
    loadLogs({
      status: filterStatus || undefined,
      relatedEntityType: filterEntityType || undefined,
    });
  };

  const handleResetFilters = () => {
    setFilterStatus('');
    setFilterEntityType('');
    loadLogs();
  };

  const handleRefresh = () => {
    loadLogs({
      status: filterStatus || undefined,
      relatedEntityType: filterEntityType || undefined,
    });
    loadStatistics();
  };

  const handleSendTestEmail = async () => {
    try {
      await sendTestEmail();
      notifications.success(t('admin.emailLogs.testSuccess'));
    } catch (err) {
      notifications.error(t('admin.emailLogs.testError'));
    }
  };

  const handleViewDetails = (log: EmailLog) => {
    setSelectedLog(log);
    setShowDetailModal(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: EmailStatus) => {
    const variants: Record<EmailStatus, 'success' | 'info' | 'warning' | 'alert' | 'pending'> = {
      sent: 'success',
      delivered: 'info',
      opened: 'info',
      clicked: 'info',
      queued: 'pending',
      failed: 'alert',
      bounced: 'warning',
    };
    const labels: Record<EmailStatus, string> = {
      sent: t('admin.emailLogs.statusSent'),
      delivered: t('admin.emailLogs.statusDelivered'),
      opened: t('admin.emailLogs.statusOpened'),
      clicked: t('admin.emailLogs.statusClicked'),
      queued: t('admin.emailLogs.statusQueued'),
      failed: t('admin.emailLogs.statusFailed'),
      bounced: t('admin.emailLogs.statusBounced'),
    };
    return <Badge variant={variants[status] || 'pending'}>{labels[status]}</Badge>;
  };

  const getEntityTypeLabel = (entityType?: string) => {
    const labels: Record<string, string> = {
      invoice: t('admin.emailLogs.entityInvoice'),
      quote: t('admin.emailLogs.entityQuote'),
      reminder: t('admin.emailLogs.entityReminder'),
      test: t('admin.emailLogs.entityTest'),
    };
    return labels[entityType || ''] || entityType || '-';
  };

  if (loading && (!logs || logs.length === 0)) {
    return (
      <div className="px-8 py-12 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-teal-100 rounded-2xl">
            <Mail className="w-8 h-8 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('admin.emailLogs.title')}</h1>
            <p className="text-gray-500 mt-0.5">{t('admin.emailLogs.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {emailServiceStatus && (
            <div className="flex items-center gap-2 px-4 py-2.5 bg-white/40 rounded-2xl border border-white/50">
              <Server className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">
                {emailServiceStatus.provider === 'mailjet' ? 'Mailjet' : 'SMTP'}
              </span>
              <span
                className={`w-2 h-2 rounded-full ${emailServiceStatus.configured ? 'bg-teal-500' : 'bg-red-500'}`}
              />
            </div>
          )}
          <Button
            onClick={handleSendTestEmail}
            variant="ghost"
            icon={<TestTube2 className="w-4 h-4" />}
          >
            {t('admin.emailLogs.test')}
          </Button>
          <Button
            onClick={handleRefresh}
            variant="ghost"
            icon={<RefreshCw className="w-4 h-4" />}
          >
            {t('admin.emailLogs.refresh')}
          </Button>
          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant={showFilters ? 'secondary' : 'ghost'}
            icon={<Filter className="w-4 h-4" />}
          >
            {t('admin.emailLogs.filters')}
          </Button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-teal-50/30 backdrop-blur-sm border border-teal-200/50 rounded-2xl p-5 hover:bg-teal-50/50 transition-all duration-300">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-teal-100 rounded-xl">
            <Info className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h4 className="font-semibold text-teal-900">{t('admin.emailLogs.trackingTitle')}</h4>
            <p className="text-sm text-teal-800 mt-1">
              {t('admin.emailLogs.trackingDesc')}
            </p>
          </div>
        </div>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="bg-red-50/50 backdrop-blur-sm border border-red-200/50 rounded-2xl p-5">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-red-100 rounded-xl">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-red-900">{t('common.labels.error')}</h4>
              <p className="text-sm text-red-800 mt-1">{error}</p>
            </div>
            <button
              onClick={clearError}
              className="p-2 text-red-600 hover:bg-red-100 rounded-xl transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Statistiques */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className={cardClasses}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.emailLogs.total')}</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.total}</p>
              </div>
              <div className="p-2 bg-gray-100 rounded-xl">
                <Mail className="w-5 h-5 text-gray-600" />
              </div>
            </div>
          </div>

          <div className={cardClasses}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.emailLogs.sent')}</p>
                <p className="text-2xl font-bold text-teal-600">{statistics.sent}</p>
              </div>
              <div className="p-2 bg-teal-100 rounded-xl">
                <CheckCircle2 className="w-5 h-5 text-teal-600" />
              </div>
            </div>
          </div>

          <div className={cardClasses}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.emailLogs.pending')}</p>
                <p className="text-2xl font-bold text-gray-600">{statistics.queued}</p>
              </div>
              <div className="p-2 bg-gray-100 rounded-xl">
                <Clock className="w-5 h-5 text-gray-600" />
              </div>
            </div>
          </div>

          <div className={cardClasses}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.emailLogs.failures')}</p>
                <p className="text-2xl font-bold text-red-600">{statistics.failed}</p>
              </div>
              <div className="p-2 bg-red-100 rounded-xl">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </div>

          <div className={cardClasses}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">{t('admin.emailLogs.bounced')}</p>
                <p className="text-2xl font-bold text-orange-600">{statistics.bounced}</p>
              </div>
              <div className="p-2 bg-orange-100 rounded-xl">
                <AlertCircle className="w-5 h-5 text-orange-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtres */}
      {showFilters && (
        <div className={cardClasses}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Filter className="w-5 h-5 text-teal-600" />
              {t('admin.emailLogs.filtersTitle')}
            </h3>
            <button
              onClick={() => setShowFilters(false)}
              className="p-2 text-gray-500 hover:bg-white/50 rounded-xl transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.labels.status')}</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as EmailStatus | '')}
                className={selectClasses}
              >
                <option value="">{t('admin.emailLogs.all')}</option>
                <option value="sent">{t('admin.emailLogs.statusSent')}</option>
                <option value="delivered">{t('admin.emailLogs.statusDelivered')}</option>
                <option value="opened">{t('admin.emailLogs.statusOpened')}</option>
                <option value="clicked">{t('admin.emailLogs.statusClicked')}</option>
                <option value="queued">{t('admin.emailLogs.statusQueued')}</option>
                <option value="failed">{t('admin.emailLogs.statusFailed')}</option>
                <option value="bounced">{t('admin.emailLogs.statusBounced')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.emailLogs.entityType')}</label>
              <select
                value={filterEntityType}
                onChange={(e) => setFilterEntityType(e.target.value)}
                className={selectClasses}
              >
                <option value="">{t('admin.emailLogs.allEntityTypes')}</option>
                <option value="invoice">{t('admin.emailLogs.entityInvoice')}</option>
                <option value="quote">{t('admin.emailLogs.entityQuote')}</option>
                <option value="reminder">{t('admin.emailLogs.entityReminder')}</option>
                <option value="test">{t('admin.emailLogs.entityTest')}</option>
              </select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={handleApplyFilters} variant="secondary" className="flex-1">
                {t('admin.emailLogs.apply')}
              </Button>
              <Button onClick={handleResetFilters} variant="ghost" className="flex-1">
                {t('admin.emailLogs.reset')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Table des emails */}
      <div className={`${cardClasses} overflow-hidden p-0`}>
        {!logs || logs.length === 0 ? (
          <div className="text-center py-12 px-6">
            <div className="p-4 bg-gray-100 rounded-2xl w-fit mx-auto mb-4">
              <Mail className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('admin.emailLogs.noEmails')}</h3>
            <p className="text-gray-600">{t('admin.emailLogs.noEmailsDesc')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/30 border-b border-white/30">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('admin.emailLogs.date')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('admin.emailLogs.recipient')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('admin.emailLogs.subject')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('admin.emailLogs.type')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('admin.emailLogs.status')}
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('admin.emailLogs.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/20">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-white/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">{formatDate(log.createdAt)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">{log.to}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-700 line-clamp-1">{log.subject}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2.5 py-1 text-xs font-medium bg-teal-50 text-teal-700 rounded-lg">
                        {getEntityTypeLabel(log.entityType)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(log.status)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleViewDetails(log)}
                        className="px-3 py-1.5 text-sm font-medium text-teal-600 hover:bg-teal-50 rounded-xl transition-colors"
                      >
                        {t('admin.emailLogs.viewDetails')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de détails */}
      {showDetailModal && selectedLog && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-white/40">
            {/* Header du modal */}
            <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">{t('admin.emailLogs.emailDetails')}</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-white hover:bg-white/20 rounded-xl p-2 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Corps du modal */}
            <div className="p-6 space-y-4 max-h-[calc(90vh-140px)] overflow-y-auto">
              <div className="p-4 bg-white/40 rounded-2xl border border-white/50">
                <label className="block text-sm font-medium text-gray-500 mb-1">{t('admin.emailLogs.recipientLabel')}</label>
                <p className="text-gray-900 font-medium">{selectedLog.to}</p>
              </div>

              <div className="p-4 bg-white/40 rounded-2xl border border-white/50">
                <label className="block text-sm font-medium text-gray-500 mb-1">{t('admin.emailLogs.subjectLabel')}</label>
                <p className="text-gray-900">{selectedLog.subject}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-white/40 rounded-2xl border border-white/50">
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('common.labels.status')}</label>
                  {getStatusBadge(selectedLog.status)}
                </div>
                <div className="p-4 bg-white/40 rounded-2xl border border-white/50">
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    {t('admin.emailLogs.entityTypeLabel')}
                  </label>
                  <p className="text-gray-900">{getEntityTypeLabel(selectedLog.entityType)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-white/40 rounded-2xl border border-white/50">
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('admin.emailLogs.createdAt')}</label>
                  <p className="text-gray-900">{formatDate(selectedLog.createdAt)}</p>
                </div>
                {selectedLog.sentAt && (
                  <div className="p-4 bg-white/40 rounded-2xl border border-white/50">
                    <label className="block text-sm font-medium text-gray-500 mb-1">{t('admin.emailLogs.sentAt')}</label>
                    <p className="text-gray-900">{formatDate(selectedLog.sentAt)}</p>
                  </div>
                )}
              </div>

              {(selectedLog.deliveredAt || selectedLog.openedAt) && (
                <div className="grid grid-cols-2 gap-4">
                  {selectedLog.deliveredAt && (
                    <div className="p-4 bg-white/40 rounded-2xl border border-white/50">
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        {t('admin.emailLogs.deliveredAt')}
                      </label>
                      <p className="text-gray-900">{formatDate(selectedLog.deliveredAt)}</p>
                    </div>
                  )}
                  {selectedLog.openedAt && (
                    <div className="p-4 bg-white/40 rounded-2xl border border-white/50">
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        {t('admin.emailLogs.openedAt')}
                      </label>
                      <p className="text-gray-900">{formatDate(selectedLog.openedAt)}</p>
                    </div>
                  )}
                </div>
              )}

              {selectedLog.clickedAt && (
                <div className="p-4 bg-white/40 rounded-2xl border border-white/50">
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('admin.emailLogs.clickedAt')}</label>
                  <p className="text-gray-900">{formatDate(selectedLog.clickedAt)}</p>
                </div>
              )}

              {selectedLog.errorMessage && (
                <div className="p-4 bg-red-50/50 rounded-2xl border border-red-200/50">
                  <label className="block text-sm font-medium text-red-700 mb-1">
                    {t('admin.emailLogs.errorMessage')}
                  </label>
                  <p className="text-red-600">{selectedLog.errorMessage}</p>
                </div>
              )}

              {selectedLog.bodyHtml && (
                <div className="p-4 bg-white/40 rounded-2xl border border-white/50">
                  <label className="block text-sm font-medium text-gray-500 mb-2">{t('admin.emailLogs.content')}</label>
                  <div
                    className="p-4 bg-white rounded-xl max-h-64 overflow-y-auto prose prose-sm"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedLog.bodyHtml) }}
                  />
                </div>
              )}
            </div>

            {/* Footer du modal */}
            <div className="p-6 border-t border-white/30">
              <Button
                onClick={() => setShowDetailModal(false)}
                variant="ghost"
                className="w-full"
              >
                {t('admin.emailLogs.close')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
